package fyi.moko.hotpl.domain.usecase

import fyi.moko.hotpl.data.image.ImageRotator
import fyi.moko.hotpl.data.image.PlaceImageStore
import fyi.moko.hotpl.domain.repository.PlaceRepository

/**
 * Turns one already-saved photo a quarter turn and puts the turned copy in its place.
 *
 * 1단계(R-1a)는 **새로 들어오는** 사진의 EXIF 방향을 굽는 것으로 끝났다. 그 전에 저장된
 * 사진은 픽셀이 이미 누운 채 구워져 있어 코드로는 되돌릴 수 없다(2026-09-08 피드백 §2.3).
 * 그 사진들을 손으로 세우는 수단이 이것이다 — 리뷰어 R-4.
 *
 * ── 왜 같은 파일에 덮어쓰지 않고 새 이름으로 저장하는가 ──────────────────────────
 * 파일명은 이 앱에서 **동기화의 열쇠**다. Firestore 문서는 이름만 들고 있고, Cloud Storage
 * 객체 키도 그 이름이며, 다른 기기는 "이름이 바뀌었을 때"만 다시 받아 온다. 같은 이름에
 * 덮어쓰면 문서가 그대로라 다른 기기는 자기 캐시에 있는 **누운 사본을 계속 쓴다.**
 * 화면 쪽도 마찬가지다 — 사진 캐시가 파일명으로 기억하므로 이름이 그대로면 방금 돌린
 * 결과가 안 보인다. 새 이름으로 저장하면 저장·업로드·문서 갱신·다른 기기 재수신·화면
 * 갱신이 **기존 경로 그대로** 굴러간다. 대신 옛 파일은 지워서 사본이 남지 않게 한다.
 *
 * ── 알려진 한계 ──────────────────────────────
 *  · JPEG을 풀었다가 다시 굽는다. 90°씩 네 번 돌려 제자리로 와도 원본과 같은 바이트가
 *    아니고, 돌릴 때마다 조금씩 뭉갠다. 세우는 데 보통 한두 번이면 되므로 감수한다.
 *  · 되돌리기는 없다. 반대로 돌리면 되는 일이라 스낵바를 따로 두지 않았다.
 */
class RotatePhotoUseCase(
    private val repository: PlaceRepository,
    private val imageStore: PlaceImageStore,
    private val rotator: ImageRotator,
) {
    /**
     * Rotates a photo in the place's gallery. Returns the new file name, or null when
     * nothing changed — the photo isn't there, its bytes are not on this device yet, or the
     * platform could not decode them.
     */
    suspend fun rotatePlacePhoto(placeId: String, fileName: String): String? {
        val place = repository.getPlace(placeId) ?: return null
        if (place.photos.none { it.fileName == fileName }) return null

        val newName = rotateAndStore(fileName) ?: return null
        repository.savePlace(
            place.copy(
                photos = place.photos.map { if (it.fileName == fileName) it.copy(fileName = newName) else it },
                imageFileName = if (place.imageFileName == fileName) newName else place.imageFileName,
            ),
        )
        imageStore.delete(fileName)
        return newName
    }

    /** The same for a photo attached to one visit. */
    suspend fun rotateVisitPhoto(visitId: String, fileName: String): String? {
        val visit = repository.getVisitIncludingDeleted(visitId) ?: return null
        if (visit.photos.none { it.fileName == fileName }) return null

        val newName = rotateAndStore(fileName) ?: return null
        repository.updateVisit(
            visit.copy(
                photos = visit.photos.map { if (it.fileName == fileName) it.copy(fileName = newName) else it },
                imageFileName = if (visit.imageFileName == fileName) newName else visit.imageFileName,
            ),
        )
        imageStore.delete(fileName)
        return newName
    }

    /**
     * [PlaceImageStore.load]를 쓰므로 이 기기에 없는 사진은 먼저 내려받아 돌린다 — 다른
     * 기기에서 넣은 사진도 세울 수 있다. 못 받으면 null이고, 그러면 문서는 손대지 않는다.
     */
    private suspend fun rotateAndStore(fileName: String): String? {
        val bytes = imageStore.load(fileName) ?: return null
        val rotated = rotator.rotate90(bytes) ?: return null
        return imageStore.save(rotated)
    }
}
